import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["typeRadio", "clientNotes", "trixEditor", "clientNotesForm", "clientNotesIcon", "approvalEmail"]
  static values = {
    milestoneId: String,
    projectSlug: String,
    highlightTask: String
  }

  connect() {
    this.updateMilestone = this.updateMilestone.bind(this)
    
    // Initialize Trix editor with existing content - with proper null checking
    if (this.hasTrixEditorTarget && this.hasClientNotesTarget) {
      const content = this.clientNotesTarget.value
      // Check if Trix editor is fully initialized before trying to use it
      if (this.trixEditorTarget.editor) {
        this.trixEditorTarget.editor.loadHTML(content)
      } else {
        // Wait for Trix to initialize then load content
        this.trixEditorTarget.addEventListener('trix-initialize', () => {
          this.trixEditorTarget.editor.loadHTML(content)
        })
      }
      // Ensure the form is hidden initially
      this.clientNotesFormTarget.classList.add('hidden')
      this.clientNotesIconTarget.style.transform = ''
    }

    // Handle task highlighting for Turbo compatibility
    if (this.hasHighlightTaskValue) {
      this.highlightTask()
    }
  }

  highlightTask() {
    // Use a longer delay and retry logic to ensure the page has fully rendered with Turbo
    const tryHighlight = (attempt = 1, maxAttempts = 5) => {
      const highlightedTask = document.getElementById(`task_row_${this.highlightTaskValue}`)
      
      if (highlightedTask) {
        // Found the element, scroll to it
        highlightedTask.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        })
        
        // Add additional highlighting after scroll completes
        setTimeout(() => {
          // Add a temporary highlight class for extra visibility
          highlightedTask.classList.add('animate-pulse')
          
          // Flash effect to draw attention
          let flashCount = 0
          const flashInterval = setInterval(() => {
            if (flashCount >= 6) { // Flash 3 times (on-off-on-off-on-off)
              clearInterval(flashInterval)
              highlightedTask.classList.remove('animate-pulse')
              return
            }
            
            if (flashCount % 2 === 0) {
              highlightedTask.style.transform = 'scale(1.02)'
              highlightedTask.style.boxShadow = '0 0 20px rgba(239, 68, 68, 0.5)'
            } else {
              highlightedTask.style.transform = 'scale(1)'
              highlightedTask.style.boxShadow = ''
            }
            flashCount++
          }, 400)
          
          // Clean up after 3 seconds
          setTimeout(() => {
            highlightedTask.style.transform = ''
            highlightedTask.style.boxShadow = ''
            highlightedTask.classList.remove('animate-pulse')
          }, 3000)
        }, 700) // Wait longer for scroll to complete
      } else if (attempt < maxAttempts) {
        // Element not found yet, try again with exponential backoff
        setTimeout(() => tryHighlight(attempt + 1, maxAttempts), 200 * attempt)
      } else {
        console.warn(`Could not find task row with ID: task_row_${this.highlightTaskValue}`)
      }
    }
    
    // Start with a longer initial delay for Turbo navigation
    setTimeout(() => tryHighlight(), 300)
  }

  updateMilestoneType(event) {
    const type = event.target.value
    this.updateMilestone({ milestone_type: type })
  }

  saveClientNotes(event) {
    const button = event.currentTarget
    const originalContent = button.innerHTML
    
    if (this.hasTrixEditorTarget) {
      const notes = this.trixEditorTarget.value
      button.innerHTML = '<iconify-icon icon="ph:circle-notch" class="text-lg animate-spin"></iconify-icon>'
      
      this.updateMilestone({ client_notes: notes })
        .then(() => {
          button.innerHTML = '<iconify-icon icon="ph:check-circle" class="text-lg"></iconify-icon> Saved'
          setTimeout(() => {
            button.innerHTML = originalContent
          }, 2000)
        })
        .catch(() => {
          button.innerHTML = originalContent
        })
    }
  }

  toggleNotes(event) {
    const taskId = event.currentTarget.dataset.taskId
    const notesRow = document.getElementById(`task_notes_row_${taskId}`)
    if (notesRow) {
      notesRow.classList.toggle('hidden')
    }
  }

  async updateMilestone(params) {
    try {
      const response = await fetch(`/projects/${this.projectSlugValue}/milestones/${this.milestoneIdValue}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ milestone: params })
      })
      
      if (!response.ok) throw new Error('Network response was not ok')
      
      const html = await response.text()
      if (html) {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const turboStreams = doc.querySelectorAll('turbo-stream')
        turboStreams.forEach(turboStream => {
          const frameId = turboStream.getAttribute('target')
          const frame = document.getElementById(frameId)
          if (frame) {
            frame.innerHTML = turboStream.querySelector('template').innerHTML
          }
        })
      }
      
      return response
    } catch (error) {
      console.error('Failed to update milestone:', error)
      throw error
    }
  }

  confirmStartMilestone(event) {
    const dialog = document.getElementById(`start_milestone_dialog_${this.milestoneIdValue}`)
    dialog.showModal()
  }

  confirmCompleteMilestone(event) {
    const dialog = document.getElementById(`complete_milestone_dialog_${this.milestoneIdValue}`)
    dialog.showModal()
  }

  confirmMoveToQA(event) {
    const dialog = document.getElementById(`move_to_qa_dialog_${this.milestoneIdValue}`)
    dialog.showModal()
  }

  confirmBackToOngoing(event) {
    const dialog = document.getElementById(`back_to_ongoing_dialog_${this.milestoneIdValue}`)
    if (dialog) dialog.showModal()
  }

  async switchToOngoing(event) {
    // Close either the start milestone dialog or the cancel completion dialog
    const startDialog = document.getElementById(`start_milestone_dialog_${this.milestoneIdValue}`)
    const cancelCompletionDialog = document.getElementById(`cancel_completion_dialog_${this.milestoneIdValue}`)
    const backToOngoingDialog = document.getElementById(`back_to_ongoing_dialog_${this.milestoneIdValue}`)
    
    if (startDialog) startDialog.close()
    if (cancelCompletionDialog) cancelCompletionDialog.close()
    if (backToOngoingDialog) backToOngoingDialog.close()
    
    this.updateMilestone({ status: 'ongoing' })
  }

  async switchToInQA(event) {
    const dialog = document.getElementById(`move_to_qa_dialog_${this.milestoneIdValue}`)
    if (dialog) dialog.close()
    this.updateMilestone({ status: 'in_qa' })
  }

  async switchToComplete(event) {
    const dialog = document.getElementById(`complete_milestone_dialog_${this.milestoneIdValue}`)
    dialog.close()
    this.updateMilestone({ status: 'complete' })
  }

  confirmSendForApproval(event) {
    const dialog = document.getElementById(`send_for_approval_dialog_${this.milestoneIdValue}`)
    dialog.showModal()
  }

  confirmRequestCompletion(event) {
    const dialog = document.getElementById(`request_completion_dialog_${this.milestoneIdValue}`)
    dialog.showModal()
  }

  confirmCancelApproval(event) {
    const dialog = document.getElementById(`cancel_approval_dialog_${this.milestoneIdValue}`)
    dialog.showModal()
  }

  confirmCancelCompletion(event) {
    const dialog = document.getElementById(`cancel_completion_dialog_${this.milestoneIdValue}`)
    dialog.showModal()
  }

  async switchToAwaitingApproval(event) {
    const dialog = document.getElementById(`send_for_approval_dialog_${this.milestoneIdValue}`)
    dialog.close()
    this.updateMilestone({ 
      status: 'awaiting_approval',
      approval_email: this.approvalEmailTarget.value
    })
  }

  async switchToAwaitingCompletion(event) {
    const dialog = document.getElementById(`request_completion_dialog_${this.milestoneIdValue}`)
    dialog.close()
    this.updateMilestone({ 
      status: 'awaiting_completion',
      approval_email: this.approvalEmailTarget.value
    })
  }

  async switchToSetup(event) {
    const dialog = document.getElementById(`cancel_approval_dialog_${this.milestoneIdValue}`)
    dialog.close()
    this.updateMilestone({ status: 'setup' })
  }

  toggleClientNotes(event) {
    this.clientNotesFormTarget.classList.toggle('hidden')
    this.clientNotesIconTarget.style.transform = this.clientNotesFormTarget.classList.contains('hidden') ? '' : 'rotate(180deg)'
  }

  adminChangeState(event) {
    const state = event.currentTarget.dataset.milestoneViewStateParam
    const dialog = event.currentTarget.closest('dialog')
    dialog.close()
    this.updateMilestone({ 
      status: state, 
      admin_update: true // This will be serialized as a boolean in the JSON request
    })
  }
}